import { useEffect, useState } from "react";
import { toast } from "sonner";
import { PageHero, Container, Overline } from "../components/Section";
import { api, API, formatApiError } from "../lib/api";
import { Upload, Trash2 } from "lucide-react";
import EventManager from "./EventManager";

const CATEGORIES = ["services", "music", "media", "gallery"];

export default function Admin() {
  const [tab, setTab] = useState("upload");
  const [media, setMedia] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [requests, setRequests] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [prayers, setPrayers] = useState([]);
  const [form, setForm] = useState({ title: "", description: "", category: "services", speaker: "", series: "" });
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [expanded, setExpanded] = useState({});

  const load = async () => {
    try {
      const [m, b, r, a, p] = await Promise.all([
        api.get("/media"),
        api.get("/bookings"),
        api.get("/media-requests"),
        api.get("/announcements").catch(() => ({ data: [] })),
        api.get("/prayer-requests").catch(() => ({ data: [] })),
      ]);
      setMedia(m.data); setBookings(b.data); setRequests(r.data); setAnnouncements(a.data); setPrayers(p.data);
    } catch (e) { /* ignore */ }
  };
  useEffect(() => { load(); }, []);

  const upload = async (e) => {
    e.preventDefault();
    if (!file) return toast.error("Choose a file");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      await api.post("/media", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Uploaded!");
      setFile(null); setForm({ title: "", description: "", category: "services", speaker: "", series: "" });
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setUploading(false); }
  };

  const del = async (id) => {
    await api.delete(`/media/${id}`);
    toast.success("Deleted");
    load();
  };

  const updateReqStatus = async (id, status) => {
    const fd = new FormData();
    fd.append("status", status);
    await api.patch(`/media-requests/${id}`, fd);
    toast.success("Updated");
    load();
  };

  const updateAnnStatus = async (id, status) => {
    const fd = new FormData();
    fd.append("status", status);
    await api.patch(`/announcements/${id}`, fd);
    toast.success("Updated");
    load();
  };

  const updatePrayerStatus = async (id, status) => {
    const fd = new FormData();
    fd.append("status", status);
    await api.patch(`/prayer-requests/${id}`, fd);
    toast.success("Updated");
    load();
  };

  const toggleExpanded = (id) => setExpanded((e) => ({ ...e, [id]: !e[id] }));

  return (
    <div data-testid="admin-page">
      <PageHero
        overline="Admin Dashboard"
        title={<>Manage <em className="text-gold-light italic font-light">everything</em>.</>}
        kicker="Upload media, review pastor bookings, and manage ministry media requests."
      />

      <Container className="pb-32">
        <div className="flex gap-2 mb-10 border-b border-white/10 flex-wrap">
          {["upload", "events", "bookings", "requests", "announcements", "prayers"].map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`px-5 py-3 text-xs uppercase tracking-widest transition-colors ${
                tab === t ? "text-gold border-b-2 border-gold -mb-px" : "text-white/60 hover:text-white"
              }`}
              data-testid={`admin-tab-${t}`}
            >
              {t === "requests" ? `Media Requests (${requests.length})` :
               t === "announcements" ? `Announcements (${announcements.length})` :
               t === "prayers" ? `Prayer Requests (${prayers.length})` :
               t === "bookings" ? `Bookings (${bookings.length})` :
               t === "events" ? "Calendar" : t}
            </button>
          ))}
        </div>

        {tab === "upload" && (
          <>
            <Overline>Upload Media</Overline>
            <form onSubmit={upload} className="glass p-8 mb-12 grid md:grid-cols-2 gap-x-8 gap-y-5 max-w-3xl" data-testid="admin-upload-form">
              <Field label="File" className="md:col-span-2">
                <input data-testid="admin-file-input" type="file" required onChange={(e) => setFile(e.target.files[0])} className="w-full text-white/80 text-sm" />
              </Field>
              <Field label="Title" required>
                <input data-testid="admin-title" required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className={cls} />
              </Field>
              <Field label="Category">
                <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className={cls} data-testid="admin-category">
                  {CATEGORIES.map((c) => <option key={c} value={c} className="bg-wine-950">{c}</option>)}
                </select>
              </Field>
              <Field label="Speaker">
                <input value={form.speaker} onChange={(e) => setForm({ ...form, speaker: e.target.value })} className={cls} />
              </Field>
              <Field label="Series">
                <input value={form.series} onChange={(e) => setForm({ ...form, series: e.target.value })} className={cls} />
              </Field>
              <Field label="Description" className="md:col-span-2">
                <textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className={cls} />
              </Field>
              <div className="md:col-span-2">
                <button disabled={uploading} className="btn-wine px-8 py-3 rounded-full text-sm uppercase tracking-widest font-semibold inline-flex items-center gap-2 disabled:opacity-50" data-testid="admin-upload-btn">
                  <Upload className="h-4 w-4" /> {uploading ? "Uploading…" : "Upload"}
                </button>
              </div>
            </form>

            <h3 className="font-serif text-2xl font-bold mb-4">All Media ({media.length})</h3>
            <div className="space-y-px bg-white/5">
              {media.map((m) => (
                <div key={m.id} className="bg-wine-950 p-5 flex items-center gap-4">
                  <div className="text-xs uppercase tracking-widest text-gold w-24">{m.category}</div>
                  <div className="flex-1">
                    <div className="font-serif text-lg">{m.title}</div>
                    <div className="text-xs text-white/50">{m.original_filename} · {(m.size / 1024 / 1024).toFixed(2)} MB</div>
                  </div>
                  <a href={`${API}/media/file/${m.id}`} target="_blank" rel="noopener noreferrer" className="text-xs uppercase tracking-widest text-gold hover:text-gold-light">View</a>
                  <button onClick={() => del(m.id)} className="text-destructive hover:text-red-400 p-2" data-testid={`admin-delete-${m.id}`}><Trash2 className="h-4 w-4" /></button>
                </div>
              ))}
            </div>
          </>
        )}

        {tab === "events" && <EventManager />}

        {tab === "bookings" && (
          <>
            <h3 className="font-serif text-2xl font-bold mb-6">Pastor Bookings ({bookings.length})</h3>
            <div className="space-y-px bg-white/5">
              {bookings.map((b) => (
                <div key={b.id} className="bg-wine-950 p-6" data-testid={`admin-booking-${b.id}`}>
                  <div className="flex justify-between items-start gap-4 flex-wrap">
                    <div>
                      <div className="font-serif text-xl font-bold">{b.event_type}</div>
                      <div className="text-sm text-white/60 mt-1">{b.name} · {b.email} · {b.phone}</div>
                      <div className="text-sm text-gold mt-2">{b.preferred_date} at {b.preferred_time}</div>
                    </div>
                    <span className="text-xs uppercase tracking-widest bg-gold/20 text-gold px-3 py-1 rounded-full">{b.status}</span>
                  </div>
                  <p className="text-sm text-white/70 mt-3">{b.details}</p>
                </div>
              ))}
            </div>
          </>
        )}

        {tab === "requests" && (
          <>
            <h3 className="font-serif text-2xl font-bold mb-2">Ministry Media Requests</h3>
            <p className="text-sm text-white/55 mb-6">Routed to LaTonya Phillips · Click any card to expand full details.</p>
            <div className="space-y-px bg-white/5">
              {requests.length === 0 && (
                <div className="bg-wine-950 p-10 text-center text-white/50 italic">No media requests yet.</div>
              )}
              {requests.map((r) => {
                const isOpen = !!expanded[r.id];
                return (
                  <div key={r.id} className="bg-wine-950 p-6" data-testid={`admin-request-${r.id}`}>
                    <div className="flex justify-between gap-4 flex-wrap items-start">
                      <button
                        type="button"
                        onClick={() => toggleExpanded(r.id)}
                        className="flex-1 text-left"
                        data-testid={`admin-request-toggle-${r.id}`}
                      >
                        <div className="text-xs uppercase tracking-widest text-gold mb-1 font-bold">
                          {r.venue || "—"} · {r.event_dates || r.needed_by || "—"}
                        </div>
                        <div className="font-serif text-xl font-bold flex items-center gap-2">
                          {r.contact_name || r.requested_by_name || "Unknown"}
                          <span className="text-white/40 text-sm">{isOpen ? "▼" : "▶"}</span>
                        </div>
                        <div className="text-xs text-white/50 mt-1">
                          {r.email || r.requested_by_email}
                          {r.phone && ` · ${r.phone}`}
                        </div>
                        {(r.media_types || []).length > 0 && (
                          <div className="flex flex-wrap gap-2 mt-3">
                            {r.media_types.map((m) => (
                              <span key={m} className="text-[10px] uppercase tracking-wider bg-wine-700/40 px-2.5 py-1 text-white/75">{m}</span>
                            ))}
                          </div>
                        )}
                      </button>
                      <select
                        value={r.status}
                        onChange={(e) => updateReqStatus(r.id, e.target.value)}
                        className="bg-wine-900 border border-white/15 text-white text-xs px-3 py-2 rounded-full uppercase tracking-widest"
                        data-testid={`admin-request-status-${r.id}`}
                      >
                        <option value="pending">Pending</option>
                        <option value="in_progress">In Progress</option>
                        <option value="completed">Completed</option>
                        <option value="declined">Declined</option>
                      </select>
                    </div>

                    {isOpen && (
                      <div className="mt-5 pt-5 border-t border-white/8 grid md:grid-cols-2 gap-x-10 gap-y-3 text-sm">
                        <Detail label="Event Dates" value={r.event_dates} />
                        <Detail label="Time" value={r.event_start_time && r.event_end_time ? `${r.event_start_time} – ${r.event_end_time}` : null} />
                        <Detail label="Venue" value={r.venue} />
                        <Detail label="Description" value={r.event_description} full />
                        <Detail label="Type of Media" value={(r.media_types || []).join(", ")} />
                        <Detail label="Wireless Handheld Mics" value={r.audio_wireless_handheld_qty} />
                        <Detail label="Wired Mics" value={r.audio_wired_mic_qty} />
                        <Detail label="Instruments" value={(r.instruments || []).join(", ")} />
                        <Detail label="Playback" value={(r.playback || []).join(", ")} />
                        <Detail label="Visual" value={(r.visual_options || []).join(", ")} full />
                        <Detail label="Additional Request" value={r.additional_request} full />
                        <Detail label="Submitted" value={r.created_at ? new Date(r.created_at).toLocaleString() : null} />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </>
        )}

        {tab === "announcements" && (
          <>
            <h3 className="font-serif text-2xl font-bold mb-6">Announcement Requests</h3>
            <div className="space-y-px bg-white/5">
              {announcements.length === 0 && (
                <div className="bg-wine-950 p-10 text-center text-white/50 italic">No announcement requests yet.</div>
              )}
              {announcements.map((a) => {
                const isOpen = !!expanded[a.id];
                return (
                  <div key={a.id} className="bg-wine-950 p-6" data-testid={`admin-announcement-${a.id}`}>
                    <div className="flex justify-between gap-4 flex-wrap items-start">
                      <button
                        type="button"
                        onClick={() => toggleExpanded(a.id)}
                        className="flex-1 text-left"
                        data-testid={`admin-announcement-toggle-${a.id}`}
                      >
                        <div className="text-xs uppercase tracking-widest text-gold mb-1 font-bold">
                          {a.channels || "—"} · {a.ministry || "—"}
                        </div>
                        <div className="font-serif text-xl font-bold flex items-center gap-2">
                          {a.announcement_title}
                          <span className="text-white/40 text-sm">{isOpen ? "▼" : "▶"}</span>
                        </div>
                        <div className="text-xs text-white/50 mt-1">
                          By {a.requested_by_name} ({a.requested_by_email}) · Need by {a.needed_by}
                        </div>
                      </button>
                      <select
                        value={a.status}
                        onChange={(e) => updateAnnStatus(a.id, e.target.value)}
                        className="bg-wine-900 border border-white/15 text-white text-xs px-3 py-2 rounded-full uppercase tracking-widest"
                        data-testid={`admin-announcement-status-${a.id}`}
                      >
                        <option value="pending">Pending</option>
                        <option value="approved">Approved</option>
                        <option value="completed">Completed</option>
                        <option value="declined">Declined</option>
                      </select>
                    </div>

                    {isOpen && (
                      <div className="mt-5 pt-5 border-t border-white/8 grid md:grid-cols-2 gap-x-10 gap-y-3 text-sm">
                        <Detail label="Announcement Text" value={a.announcement_text} full />
                        <Detail label="Channels" value={a.channels} />
                        <Detail label="Event Date" value={a.event_date} />
                        <Detail label="Needed By" value={a.needed_by} />
                        <Detail label="Contact" value={a.contact_email} />
                        <Detail label="Submitted" value={a.created_at ? new Date(a.created_at).toLocaleString() : null} />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </>
        )}

        {tab === "prayers" && (
          <>
            <h3 className="font-serif text-2xl font-bold mb-2">🙏🏾 Prayer Requests</h3>
            <p className="text-sm text-white/55 mb-6">Public submissions from the Prayer Request page. Click any card to expand full details.</p>
            <div className="space-y-px bg-white/5">
              {prayers.length === 0 && (
                <div className="bg-wine-950 p-10 text-center text-white/50 italic">No prayer requests yet.</div>
              )}
              {prayers.map((p) => {
                const isOpen = !!expanded[p.id];
                return (
                  <div key={p.id} className="bg-wine-950 p-6" data-testid={`admin-prayer-${p.id}`}>
                    <div className="flex justify-between gap-4 flex-wrap items-start">
                      <button
                        type="button"
                        onClick={() => toggleExpanded(p.id)}
                        className="flex-1 text-left"
                        data-testid={`admin-prayer-toggle-${p.id}`}
                      >
                        <div className="text-xs uppercase tracking-widest text-gold mb-1 font-bold flex items-center gap-2">
                          {p.is_private && <span className="text-[10px] bg-gold/20 text-gold px-2 py-0.5 rounded-full">🔒 Private</span>}
                          {p.created_at ? new Date(p.created_at).toLocaleString() : ""}
                        </div>
                        <div className="font-serif text-xl font-bold flex items-center gap-2">
                          {p.name || "Anonymous"}
                          <span className="text-white/40 text-sm">{isOpen ? "▼" : "▶"}</span>
                        </div>
                        <p className="text-sm text-white/70 mt-2 line-clamp-2 max-w-3xl">{p.prayer_request}</p>
                      </button>
                      <select
                        value={p.status}
                        onChange={(e) => updatePrayerStatus(p.id, e.target.value)}
                        className="bg-wine-900 border border-white/15 text-white text-xs px-3 py-2 rounded-full uppercase tracking-widest"
                        data-testid={`admin-prayer-status-${p.id}`}
                      >
                        <option value="pending">Pending</option>
                        <option value="printed">Printed for Altar</option>
                        <option value="prayed">Prayed Over</option>
                        <option value="answered">Answered</option>
                        <option value="archived">Archived</option>
                      </select>
                    </div>

                    {isOpen && (
                      <div className="mt-5 pt-5 border-t border-white/8 grid md:grid-cols-2 gap-x-10 gap-y-3 text-sm">
                        <Detail label="Prayer Request" value={p.prayer_request} full />
                        <Detail label="Additional Info" value={p.additional_info} full />
                        <Detail label="Name" value={p.name || "Anonymous"} />
                        <Detail label="Phone" value={p.phone} />
                        <Detail label="Email" value={p.email} />
                        <Detail label="Privacy" value={p.is_private ? "Private — Pastor & prayer team only" : "Shared with prayer team"} />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </>
        )}
      </Container>
    </div>
  );
}

function Detail({ label, value, full = false }) {
  if (value === null || value === undefined || value === "" || value === 0) return null;
  return (
    <div className={full ? "md:col-span-2" : ""}>
      <div className="text-[10px] uppercase tracking-[0.25em] text-white/45 font-semibold mb-1">{label}</div>
      <div className="text-white/85 leading-relaxed">{value}</div>
    </div>
  );
}

const cls = "w-full bg-transparent border-0 border-b border-white/15 px-0 py-2 focus:outline-none focus:border-gold text-sm";
function Field({ label, required, children, className = "" }) {
  return (
    <label className={`block ${className}`}>
      <div className="text-xs uppercase tracking-widest text-white/55 mb-2 font-semibold">{label}{required && <span className="text-gold ml-1">*</span>}</div>
      {children}
    </label>
  );
}
